const en = {
  // ── Sections shared by more than one page ────────────────────────────────
  process: {
    eyebrow: "Our learning process",
    title: "A learning path shaped around you",
    aside:
      "Every learner is different. We meet you where you are and build a path from your first line of code to real-world fluency.",
    steps: [
      {
        title: "Assessment",
        body: "We assess where you are today and shape the course around your goals, from first-time coders to working developers.",
      },
      {
        title: "Learning",
        body: "Live, hands-on sessions with real-time guidance and projects drawn from real engineering work.",
      },
      {
        title: "Mastery",
        body: "Ongoing feedback and support as you sharpen your skills and start applying them on your own.",
      },
    ],
  },

  whyChoose: {
    eyebrow: "Why choose us",
    title: "The difference is in how we teach",
    items: [
      {
        title: "Expert instructors",
        body: "Working software engineers who teach. Every instructor has shipped production code and brings that experience into the classroom.",
      },
      {
        title: "Learning at your pace",
        body: "Courses adapt to your goals and your pace, so you spend your time on the material that moves you forward.",
      },
      {
        title: "Proven results",
        body: "Learners leave able to build things. The hands-on format means you practise the skill you came for, not just read about it.",
      },
    ],
  },

  // ── Home ─────────────────────────────────────────────────────────────────
  home: {
    metaTitle: "SkillSynergy | Live coding and AI courses",
    metaDescription:
      "Live, hands-on programming and AI courses for every level. Learn software engineering step by step with expert instructors, in group or one-to-one classes.",

    hero: {
      badge: "Live online courses",
      titleLine1: "From beginner",
      titleLine2: "to pro. Master",
      titleCoding: "coding",
      titleAnd: "&",
      titleAi: "AI",
      lead: "Live, hands-on programming courses for every level, now including AI and machine learning. Learn step by step with expert instructors and build real confidence.",
      rating: "Loved by learners worldwide",
      yearsValue: "10+",
      yearsLabel: "Years experience",
      imageAlt: "An instructor guiding a student through code on a laptop",
    },

    aiSpotlight: {
      badge: "New course",
      titlePre: "Learn",
      titleAi: "AI",
      titlePost: "& Machine Learning",
      body: "Tech's most in-demand skill, taught live and hands-on. Go from machine learning foundations to shipping real applications with LLMs and AI agents.",
      exploreCta: "Explore the AI course",
    },

    skills: {
      eyebrow: "What you'll master",
      title: "Skills that build real careers",
    },

    pricing: {
      eyebrow: "Pricing & packaging",
      title: "Simple plans, serious progress",
    },

    cta: {
      title: "Build real coding skills with live courses",
      body: "Book a free consultation and we'll help you choose the right path.",
    },
  },

  // ── Courses index ────────────────────────────────────────────────────────
  coursesPage: {
    metaTitle: "SkillSynergy | Courses",
    metaDescription:
      "Personalized and group-based online programming courses for all skill levels, through flexible, structured learning paths with varying intensity.",

    eyebrow: "Our catalogue",
    title: "Courses built for real progress",
    lead: "Personalized and group-based online programming courses for all skill levels, through flexible, structured learning paths with varying intensity.",
    statCoursesValue: "7",
    statCoursesLabel: "Courses offered",
    statYearsValue: "10+",
    statYearsLabel: "Years experience",
    coreTitle: "Our core courses",
    heroImageAlt: "Students working through a programming exercise together",
  },

  // ── Course detail ────────────────────────────────────────────────────────
  coursePage: {
    curriculumEyebrow: "Curriculum",
    curriculumTitle: "What you'll learn",
    aboutTitle: "About this course",
    exploreOthers: "Explore other courses",
    readyTitle: (course: string) => `Ready to start ${course}?`,
    readyBody: "Book a free consultation and get a personalized plan.",
  },

  // ── About ────────────────────────────────────────────────────────────────
  about: {
    metaTitle: "SkillSynergy | About us",
    metaDescription:
      "SkillSynergy runs interactive, live programming courses for learners at every level, including complete beginners. Small classes, taught by working engineers.",

    eyebrow: "About SkillSynergy",
    title: "We teach people to write real software",
    lead: "Interactive, live programming courses designed for learners of all levels, even from zero.",
    storyEyebrow: "About SkillSynergy",
    storyTitle: "Your trusted guide to programming",
    storyBody1:
      "SkillSynergy runs interactive, live programming courses for learners at every level, including complete beginners. Classes are small, hands-on, and taught by working engineers.",
    storyBody2:
      "The goal is straightforward: you finish able to build software on your own, and you have someone to ask when you get stuck.",
    cta: {
      title: "Let's build your skills together",
      body: "Learn alongside other people, with expert guidance.",
    },
    imageAlt: [
      "A student pair-programming at a laptop",
      "An instructor reviewing code on a whiteboard",
      "A study group working through an exercise",
    ],
    storyImageAlt: "A developer working through a problem at a laptop",
  },

  // ── Pricing ──────────────────────────────────────────────────────────────
  pricingPage: {
    metaTitle: "SkillSynergy | Pricing",
    metaDescription:
      "Choose a group class for collaborative learning, or go individual for fully personalized mentorship. Light, Semi-Intensive and Intensive tracks.",

    offerBadge: (offer: string) =>
      `Launch offer · ${offer}% off for the first cohorts`,
    title: "Providing the best pricing for you",
    lead: "Choose a group class for collaborative learning, or go individual for fully personalized mentorship.",
    perMonth: "/mo",
    priceNote:
      "All prices are per month and include VAT where it applies. No enrollment fee.",
    popular: "Popular",
    group: "Group",
    individual: "Individual",
    planLabel: "Billing plan",
    cta: {
      title: "Not sure which plan fits?",
      body: "Book a free consultation and we'll recommend the right track for your goals.",
    },
  },

  // ── Contact ──────────────────────────────────────────────────────────────
  contact: {
    metaTitle: "SkillSynergy | Contact",
    metaDescription:
      "Get in touch and our team will help you find the right course and get started. We usually reply within one business day.",

    eyebrow: "Getting in touch",
    title: "Want to learn more about our courses?",
    lead: "Reach out and our team will help you find the right course and get started. We usually reply within one business day.",
    phone: "Phone",
    email: "Email",
    location: "Location",
    formTitle: "Send us a message",
    fullName: "Full Name",
    fullNamePlaceholder: "Jane Doe",
    emailLabel: "Email",
    emailPlaceholder: "jane@email.com",
    subject: "Subject",
    subjectPlaceholder: "Course enquiry",
    message: "Message",
    messagePlaceholder: "Tell us about your goals...",
    send: "Send message",
    sending: "Sending…",
    successTitle: "Message sent",
    successBody:
      "Thanks for reaching out. We'll reply within one business day.",
    error: "That didn't go through. Please try again or email us directly.",
  },
};

export default en;
